import { create } from 'zustand';
import type { RenderMode } from '../utils/MaterialManager';
import type { CellColormap } from '../utils/cellColormap';

// Scene configuration interface - extensible for future UI controls
export interface SceneConfig {
  // Background settings
  backgroundColor: string;

  // Camera settings (for future UI controls)
  cameraSpeed: number;
  autoRotate: boolean;
  cameraZoomOutFactor: number; // Multiplier for auto-zoom distance (higher = more zoomed out)

  // Camera state persistence - preserves camera across navigation
  cameraPosition: { x: number; y: number; z: number };
  cameraTarget: { x: number; y: number; z: number };
  cameraZoom: number; // Distance from target

  // Brightness/lighting controls
  cellBrightness: number;     // 0.1 to 3.0 - multiplier for cell colors
  layerBrightness: number;    // 0.1 to 2.0 - multiplier for layer opacity
  ambientLightIntensity: number; // 0.0 to 2.0 - ambient light strength
  sceneExposure: number;      // 0.5 to 3.0 - overall scene exposure
  cellTransparency: number;   // 0.1 to 1.0 - cell transparency/opacity control
  cellSizeScale: number;      // 0.1 to 2.0 - multiplier for all cell radii
  cellColormap: CellColormap; // What the cell colors represent
  bloomStrength: number;      // 0.0 to 3.0 - strength of the blurred glow around cells (0 = off)

  // Visibility toggles, by cell type and by layer key (missing = visible)
  cellTypeVisibility: Record<string, boolean>;

  // Brain layer visibility toggles
  layerVisibility: Record<string, boolean>;
  
  // Brain layer content visibility (cells within layers) - separate from border visibility
  layerContentVisibility: Record<string, boolean>;

  // Performance settings (for future UI controls)
  sphereQuality: 'low' | 'medium' | 'high' | 'very-high' | 'ultra';

  // Cell rendering mode - CENTRALIZED material management
  cellRenderMode: RenderMode; // Replaces use3DRendering with proper mode selection
  use3DRendering: boolean; // DEPRECATED: Keep for backward compatibility, will be removed
  // Note: When in texture mode, always uses cell_texture_128.png (no picker needed)
  // Note: When in transparent_3d mode, always uses Original 3D template (template 0)

  // Texture mode blending options
  textureBlendingMode: 'normal' | 'additive' | 'multiply' | 'screen' | 'overlay';

  // UI state management
  showHelp: boolean; // Help overlay visibility
  themeMode: 'dark' | 'light'; // Theme mode toggle

  // Internal flags
  _brightnessValidated: boolean; // Internal flag to prevent resetting user values on navigation
}

// Default configuration
const defaultConfig: SceneConfig = {
  backgroundColor: '#000000', // Pure black (main scene remains black)
  cameraSpeed: 0.05,
  autoRotate: false,
  cameraZoomOutFactor: 1.42, // 1.42 = optimal view for 232.3 units distance, 3.5 = closer view, 10.0 = ultra wide view

  // Default camera state - matches RenderScene initial setup
  cameraPosition: { x: -102.60, y: 140.00, z: 192.72 }, // Raised camera Y position for better top view
  cameraTarget: { x: 31.63, y: 30.0, z: -3.53 }, // Lowered Y target for top alignment
  cameraZoom: 1.0, // Default zoom level

  cellBrightness: 1.0,        // Default cell brightness
  layerBrightness: 1.0,       // Layer edge brightness
  ambientLightIntensity: 1.0, // Neutral ambient light - no boost or reduction
  sceneExposure: 1.4,         // Enhanced exposure for better visibility
  cellTransparency: 0.9,      // Default transparency (90% opaque)
  cellSizeScale: 1.0,
  cellColormap: 'mtypes',
  bloomStrength: 0.0,
  cellTypeVisibility: {},
  layerVisibility: {},
  layerContentVisibility: {},
  sphereQuality: 'high',  // Changed to 'high' to match shader default of 1.0
  cellRenderMode: 'solid_plastic', // Default to shaded opaque spheres
  textureBlendingMode: 'additive', // Default to additive blending for textures
  use3DRendering: true, // DEPRECATED: Keep for backward compatibility
  showHelp: false, // Help overlay starts closed
  themeMode: 'dark', // Default to dark theme
  _brightnessValidated: false, // Start as false so initial validation runs once
};

// Zustand store for scene configuration
export interface SceneConfigStore extends SceneConfig {
  // Actions to update configuration
  setBackgroundColor: (color: string) => void;
  setCameraSpeed: (speed: number) => void;
  setCameraZoomOutFactor: (factor: number) => void;
  setAutoRotate: (rotate: boolean) => void;

  // Camera state persistence actions
  setCameraPosition: (position: { x: number; y: number; z: number }) => void;
  setCameraTarget: (target: { x: number; y: number; z: number }) => void;
  setCameraZoom: (zoom: number) => void;
  saveCameraState: (position: { x: number; y: number; z: number }, target: { x: number; y: number; z: number }, zoom: number) => void;

  setCellBrightness: (brightness: number) => void;
  setLayerBrightness: (brightness: number) => void;
  setAmbientLightIntensity: (intensity: number) => void;
  setSceneExposure: (exposure: number) => void;
  setCellTransparency: (transparency: number) => void;
  setCellSizeScale: (scale: number) => void;
  setCellColormap: (colormap: CellColormap) => void;
  setBloomStrength: (strength: number) => void;
  toggleCellType: (cellType: keyof SceneConfig['cellTypeVisibility']) => void;
  toggleLayer: (layer: keyof SceneConfig['layerVisibility']) => void;
  toggleLayerContent: (layer: keyof SceneConfig['layerContentVisibility']) => void;
  setSphereQuality: (quality: 'low' | 'medium' | 'high' | 'very-high' | 'ultra') => void;
  setCellRenderMode: (mode: RenderMode) => void; // NEW: Centralized render mode
  setTextureBlendingMode: (mode: 'normal' | 'additive' | 'multiply' | 'screen' | 'overlay') => void;
  setUse3DRendering: (use3D: boolean) => void; // DEPRECATED: Keep for backward compatibility

  // UI state actions
  setShowHelp: (show: boolean) => void;
  toggleHelp: () => void;
  setThemeMode: (mode: 'dark' | 'light') => void;
  toggleTheme: () => void;

  resetToDefaults: () => void;
  resetVisibilityToDefaults: () => void;
  _setBrightnessValidated: (validated: boolean) => void; // Internal setter
}

export const useSceneConfig = create<SceneConfigStore>((set, get) => ({
  ...defaultConfig,

  // Action creators
  setBackgroundColor: (color: string) => set({ backgroundColor: color }),

  setCameraSpeed: (speed: number) => set({ cameraSpeed: Math.max(0.01, Math.min(0.1, speed)) }),
  setCameraZoomOutFactor: (factor: number) => set({ cameraZoomOutFactor: Math.max(1.0, Math.min(20.0, factor)) }),

  setAutoRotate: (rotate: boolean) => set({ autoRotate: rotate }),

  // Camera state persistence actions
  setCameraPosition: (position: { x: number; y: number; z: number }) => set({ cameraPosition: position }),

  setCameraTarget: (target: { x: number; y: number; z: number }) => set({ cameraTarget: target }),

  setCameraZoom: (zoom: number) => set({ cameraZoom: zoom }),

  saveCameraState: (position: { x: number; y: number; z: number }, target: { x: number; y: number; z: number }, zoom: number) =>
    set({ cameraPosition: position, cameraTarget: target, cameraZoom: zoom }),

  setCellBrightness: (brightness: number) => set({ cellBrightness: Math.max(0.1, Math.min(3.0, brightness)) }),

  setLayerBrightness: (brightness: number) => set({ layerBrightness: Math.max(0.1, Math.min(2.0, brightness)) }),

  setAmbientLightIntensity: (intensity: number) => set({ ambientLightIntensity: Math.max(0.0, Math.min(2.0, intensity)) }),

  setSceneExposure: (exposure: number) => set({ sceneExposure: Math.max(0.5, Math.min(3.0, exposure)) }),

  setCellTransparency: (transparency: number) => set({ cellTransparency: Math.max(0.1, Math.min(1.0, transparency)) }),

  setCellSizeScale: (scale: number) => set({ cellSizeScale: Math.max(0.1, Math.min(2.0, scale)) }),

  setCellColormap: (colormap: CellColormap) => set({ cellColormap: colormap }),

  setBloomStrength: (strength: number) => set({ bloomStrength: Math.max(0.0, Math.min(3.0, strength)) }),

  toggleCellType: (cellType: keyof SceneConfig['cellTypeVisibility']) => set((state) => ({
    cellTypeVisibility: {
      ...state.cellTypeVisibility,
      [cellType]: !(state.cellTypeVisibility[cellType] ?? true),
    },
  })),

  toggleLayer: (layer: keyof SceneConfig['layerVisibility']) => set((state) => ({
    layerVisibility: {
      ...state.layerVisibility,
      [layer]: !(state.layerVisibility[layer] ?? true),
    },
  })),

  toggleLayerContent: (layer: keyof SceneConfig['layerContentVisibility']) => set((state) => ({
    layerContentVisibility: {
      ...state.layerContentVisibility,
      [layer]: !(state.layerContentVisibility[layer] ?? true),
    },
  })),

  setSphereQuality: (quality: 'low' | 'medium' | 'high' | 'very-high' | 'ultra') => set({ sphereQuality: quality }),

  setCellRenderMode: (mode: RenderMode) => set({ cellRenderMode: mode }),
  setTextureBlendingMode: (mode: 'normal' | 'additive' | 'multiply' | 'screen' | 'overlay') => set({ textureBlendingMode: mode }),

  setUse3DRendering: (use3D: boolean) => set({ use3DRendering: use3D }), // DEPRECATED: Keep for backward compatibility

  // UI state actions
  setShowHelp: (show: boolean) => set({ showHelp: show }),

  toggleHelp: () => set((state) => ({ showHelp: !state.showHelp })),
  setThemeMode: (mode: 'dark' | 'light') => set({ themeMode: mode }),
  toggleTheme: () => set((state) => ({ themeMode: state.themeMode === 'dark' ? 'light' : 'dark' })),

  resetToDefaults: () => set(defaultConfig),

  resetVisibilityToDefaults: () => set({
    layerVisibility: {},
    layerContentVisibility: {},
    cellTypeVisibility: {},
  }),

  _setBrightnessValidated: (validated: boolean) => set({ _brightnessValidated: validated }),
}));

// Utility functions for converting between formats
export const hexToThreeColor = (hex: string): number => {
  return parseInt(hex.replace('#', ''), 16);
};

// Whether a background color is light (cells are then drawn darker/inverted)
export const isLightBackground = (hex: string): boolean => {
  const value = hexToThreeColor(hex);
  const luminance =
    0.299 * ((value >> 16) & 0xff) + 0.587 * ((value >> 8) & 0xff) + 0.114 * (value & 0xff);
  return luminance > 127.5;
};

export const threeColorToHex = (color: number): string => {
  return `#${color.toString(16).padStart(6, '0')}`;
};

// Quick background color presets for easy testing
export const BACKGROUND_PRESETS = [
  { name: 'Black', color: '#000000' },
  { name: 'White', color: '#ffffff' },
] as const;

// Global exposure for performance monitoring
if (typeof window !== 'undefined') {
  (window as any).useSceneConfig = useSceneConfig;
}
